/**
 * Lightweight tween system for smooth animations.
 * Handles property interpolation with easing functions.
 */

export type EasingFn = (t: number) => number;

export const Easing = {
  linear: (t: number) => t,

  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),

  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => (--t) * t * t + 1,
  easeInOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),

  easeOutBounce: (t: number) => {
    if (t < 1 / 2.75) return 7.5625 * t * t;
    if (t < 2 / 2.75) return 7.5625 * (t -= 1.5 / 2.75) * t + 0.75;
    if (t < 2.5 / 2.75) return 7.5625 * (t -= 2.25 / 2.75) * t + 0.9375;
    return 7.5625 * (t -= 2.625 / 2.75) * t + 0.984375;
  },

  easeOutElastic: (t: number) => {
    if (t === 0 || t === 1) return t;
    return Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
  },

  easeOutBack: (t: number) => {
    const s = 1.70158;
    return (t -= 1) * t * ((s + 1) * t + s) + 1;
  },
};

interface ActiveTween {
  target: Record<string, number>;
  property: string;
  from: number;
  to: number;
  duration: number;
  elapsed: number;
  easing: EasingFn;
  onComplete?: () => void;
  onUpdate?: (value: number) => void;
}

export class TweenManager {
  private tweens: ActiveTween[] = [];

  /**
   * Start a new tween.
   * @param target - Object whose property will be animated
   * @param property - Property name on the target
   * @param from - Start value
   * @param to - End value
   * @param duration - Duration in milliseconds
   * @param easing - Easing function (default: easeOutQuad)
   * @param onComplete - Callback when tween finishes
   */
  tween(
    target: Record<string, number>,
    property: string,
    from: number,
    to: number,
    duration: number,
    easing: EasingFn = Easing.easeOutQuad,
    onComplete?: () => void,
    onUpdate?: (value: number) => void,
  ): void {
    // Remove any existing tween on the same target+property
    this.tweens = this.tweens.filter(
      (t) => !(t.target === target && t.property === property),
    );

    target[property] = from;

    this.tweens.push({
      target,
      property,
      from,
      to,
      duration,
      elapsed: 0,
      easing,
      onComplete,
      onUpdate,
    });
  }

  /**
   * Update all active tweens.
   * @param deltaMs - Time elapsed since last update in milliseconds
   */
  update(deltaMs: number): void {
    const completed: ActiveTween[] = [];

    for (const tween of this.tweens) {
      tween.elapsed += deltaMs;
      const t = Math.min(tween.elapsed / tween.duration, 1);
      const easedT = tween.easing(t);
      const value = tween.from + (tween.to - tween.from) * easedT;

      tween.target[tween.property] = value;
      tween.onUpdate?.(value);

      if (t >= 1) {
        tween.target[tween.property] = tween.to;
        completed.push(tween);
      }
    }

    // Remove completed tweens and fire callbacks
    for (const tween of completed) {
      const index = this.tweens.indexOf(tween);
      if (index !== -1) this.tweens.splice(index, 1);
      tween.onComplete?.();
    }
  }

  /** Number of active tweens. */
  get activeCount(): number {
    return this.tweens.length;
  }

  /** Cancel all tweens on a specific target. */
  cancelTarget(target: Record<string, number>): void {
    this.tweens = this.tweens.filter((t) => t.target !== target);
  }

  /** Cancel all tweens. */
  cancelAll(): void {
    this.tweens = [];
  }
}
